// Weekly student report — the PDF a school sends home. Written for the
// parent: page 1 is the week (wins, where to focus, what to do at home),
// page 2 is a letter written by the AI from the same facts, then every
// statistic the panel holds. Colours are the panel's tokens; the logo is YADA's.
import jsPDF from 'jspdf';
import type {
  StudentOverview, AcademicResultRow, StudentWeeklyTrendResponse,
  StudentChapterMasteryResponse, CommitmentsResponse, StudentEngagementResponse,
  StudentParentResponse, StudentCycleRow,
} from '../../../api/types';
import { plural } from '../../../lib/plural';

export interface StudentReportData {
  schoolName: string;
  generatedAt: string;                 // ISO date
  overview: StudentOverview;
  academic: { results: AcademicResultRow[] };
  trend: StudentWeeklyTrendResponse | null;
  mastery: StudentChapterMasteryResponse | null;
  commitments: CommitmentsResponse | null;
  engagement: StudentEngagementResponse | null;
  parent: StudentParentResponse | null;
  cycles: { cycles: StudentCycleRow[] } | null;
  /** PNG data URL, already downscaled. Optional so the builder works without a DOM. */
  logoDataUrl?: string;
  /** The AI-written letter; when missing the PDF says so instead of inventing one. */
  letter?: string | null;
}

export interface WeekHighlights {
  weekOf: string;
  wins: string[];
  /** Where the student should focus (concerns, reframed for the family). */
  focus: string[];
  /** What the family can do at home this week. */
  actions: string[];
}

// Panel tokens (tailwind.config.cjs)
const C = {
  teal: [60, 123, 123] as const,      // secondary
  gold: [196, 155, 58] as const,      // primary
  dark: [42, 77, 77] as const,        // boxdark
  body: [100, 116, 139] as const,
  muted: [138, 153, 175] as const,    // bodydark2
  stroke: [226, 232, 240] as const,
  gray2: [247, 249, 252] as const,
  success: [16, 185, 129] as const,   // meta-3
  warning: [255, 167, 11] as const,
  danger: [211, 64, 83] as const,
  white: [255, 255, 255] as const,
};
type Rgb = readonly [number, number, number];

const PAGE_W = 595.28, PAGE_H = 841.89, M = 40, FOOTER_H = 36;

function mondayOf(d: Date): string {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() - day + 1);
  return x.toISOString().slice(0, 10);
}
const pct = (v: number | null | undefined, d = 0) => (v == null ? '—' : `${v.toFixed(d)}%`);
const fullName = (o: StudentOverview) =>
  `${o.profile.first_name} ${o.profile.last_name}`.trim() || o.profile.username;
const firstName = (o: StudentOverview) => o.profile.first_name || o.profile.username;
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');

function masteryStatus(p: number | null | undefined): string {
  if (p == null) return 'not tried yet';
  if (p < 50) return 'needs work';
  if (p < 70) return 'getting there';
  if (p < 85) return 'good';
  return 'strong';
}

/** The week's wins, focus areas and at-home actions, derived from every block we hold. */
export function deriveWeekHighlights(d: StudentReportData, now: Date = new Date()): WeekHighlights {
  const wins: string[] = [], focus: string[] = [], actions: string[] = [];
  const o = d.overview;
  const first = firstName(o);
  const weekOf = o.weekly_summary?.week_of ?? d.parent?.latest_digest?.week_start
    ?? d.commitments?.current_week_start ?? mondayOf(now);
  const weekAgo = now.getTime() - 7 * 86400000;

  for (const b of o.weekly_summary?.bullets ?? []) {
    if (b.tone === 'strength') wins.push(b.text);
    else if (b.tone === 'concern') focus.push(b.text);
    else actions.push(b.text);
  }

  const overall = d.trend?.overall ?? [];
  if (overall.length >= 2) {
    const last = overall[overall.length - 1], prev = overall[overall.length - 2];
    const delta = last.avg_score - prev.avg_score;
    if (delta >= 3) wins.push(`${first}'s weekly average rose from ${pct(prev.avg_score)} to ${pct(last.avg_score)}.`);
    else if (delta <= -3) focus.push(`The weekly average fell from ${pct(prev.avg_score)} to ${pct(last.avg_score)}.`);
  }

  const thisWeek = d.academic.results.filter((r) => new Date(r.created).getTime() >= weekAgo);
  if (thisWeek.length > 0) {
    const scored = thisWeek.map((r) => r.score_percentage).filter((v): v is number => v != null);
    const best = scored.length ? Math.max(...scored) : null;
    wins.push(`${first} took ${plural(thisWeek.length, 'exam')} this week${best != null ? ` (best ${pct(best)})` : ''}.`);
    const low = thisWeek.filter((r) => r.score_percentage != null && r.score_percentage < 50);
    if (low.length) {
      focus.push(`${plural(low.length, 'exam')} this week scored below 50%: ${low.map((r) => r.exam_name ?? r.chapter ?? 'exam').join(', ')}.`);
    }
  } else {
    focus.push('No exams were taken this week.');
    actions.push(`Encourage ${first} to take at least one practice exam this week; practice tests are how progress is measured.`);
  }

  for (const a of o.anomalies ?? []) {
    if (a.resolved_at) continue;
    if (a.anomaly_type === 'spike') wins.push(a.description);
    else focus.push(a.description);
  }

  const items = d.commitments?.plan_items ?? [];
  if (items.length) {
    const done = items.filter((i) => i.status === 'completed');
    const skipped = items.filter((i) => i.status === 'skipped');
    if (done.length) wins.push(`Completed ${done.length} of ${items.length} study-plan items this week.`);
    if (skipped.length) {
      focus.push(`Skipped ${plural(skipped.length, 'study-plan item')} this week.`);
      actions.push(`Ask about the ${skipped.map((i) => `${i.day} ${i.subject_name}`).join(' and ')} study-plan ${skipped.length === 1 ? 'item' : 'items'} that ${skipped.length === 1 ? 'was' : 'were'} skipped, and help find a time for ${skipped.length === 1 ? 'it' : 'them'}.`);
    }
  }

  const e = d.engagement?.kpis;
  if (e) {
    const weeklyAvg30 = e.total_study_minutes_30d / 4;
    if (e.total_study_minutes_7d === 0) {
      focus.push('No study time was recorded this week.');
      actions.push('Agree a fixed daily study slot, even 20 minutes after dinner, and check that it happened.');
    } else if (weeklyAvg30 > 0 && e.total_study_minutes_7d >= weeklyAvg30 * 1.15) {
      wins.push(`${first} studied ${e.total_study_minutes_7d} minutes this week, above the monthly average.`);
    } else if (weeklyAvg30 > 0 && e.total_study_minutes_7d <= weeklyAvg30 * 0.7) {
      focus.push(`Study time dropped to ${e.total_study_minutes_7d} minutes this week, below the monthly average of about ${Math.round(weeklyAvg30)}.`);
      actions.push(`A short daily study slot would bring study time back to about ${Math.round(weeklyAvg30)} minutes a week.`);
    }
  }

  for (const a of d.parent?.latest_digest?.payload.areas_to_support ?? []) {
    focus.push(`${a.chapter ?? 'A chapter'}${a.subject ? ` (${a.subject})` : ''} needs support${a.mastery_pct != null ? `: currently ${a.mastery_pct}%` : ''}.`);
  }

  if (d.mastery) {
    const names: Record<number, string> = {};
    const cls: Record<number, number | null> = {};
    d.mastery.chapters.forEach((c) => { names[c.id] = c.name; });
    d.mastery.class_avg_per_chapter.forEach((c) => { cls[c.chapter_id] = c.pct; });
    const weak = [...d.mastery.student_cells].filter((c) => c.pct < 50).sort((a, b) => a.pct - b.pct).slice(0, 3);
    for (const c of weak) {
      const name = names[c.chapter_id] ?? 'a chapter';
      const cp = cls[c.chapter_id];
      focus.push(`${name} is at ${pct(c.pct)}${cp != null ? `, while the class averages ${pct(cp)}` : ''}.`);
    }
    if (weak.length) {
      actions.push(`Set aside 30 minutes, three times this week, for ${weak.map((c) => names[c.chapter_id] ?? 'the weak chapter').join(' and ')}, then a short practice quiz on ${weak.length === 1 ? 'it' : 'each'}.`);
    }
    const strong = [...d.mastery.student_cells].filter((c) => c.pct >= 85);
    if (strong.length) wins.push(`${first} has mastered ${plural(strong.length, 'chapter')} at 85% or better: ${strong.slice(0, 3).map((c) => names[c.chapter_id] ?? 'chapter').join(', ')}.`);
  }

  for (const c of o.study_plan?.chapters.slice(0, 3) ?? []) {
    actions.push(`This week's study plan says ${c.name} (${c.subject}): ${c.rationale}. Check it is done by the weekend.`);
  }
  for (const x of d.parent?.latest_digest?.payload.upcoming_exams ?? []) {
    actions.push(`Ask about the ${x.name}${x.subject ? ` (${x.subject})` : ''} due ${x.due_date}; a short review the evening before helps.`);
  }
  actions.push('Look at the study plan together on Sunday and tick off what was done.');
  actions.push('Praise the wins above; encouragement works better than pressure.');

  const dedupe = (xs: string[]) => Array.from(new Set(xs));
  return { weekOf, wins: dedupe(wins), focus: dedupe(focus), actions: dedupe(actions).slice(0, 7) };
}

/** The FACTS the AI letter is written from: the same numbers and lists the PDF shows. */
export function buildLetterFacts(d: StudentReportData, h: WeekHighlights, now: Date = new Date()): Record<string, unknown> {
  const o = d.overview;
  const e = d.engagement?.kpis;
  const weekAgo = now.getTime() - 7 * 86400000;
  const names: Record<number, string> = {};
  d.mastery?.chapters.forEach((c) => { names[c.id] = c.name; });
  const cls: Record<number, number | null> = {};
  d.mastery?.class_avg_per_chapter.forEach((c) => { cls[c.chapter_id] = c.pct; });
  return {
    student_first_name: firstName(o),
    school_name: d.schoolName,
    grade: o.profile.school_grade,
    section: o.section?.display ?? null,
    week_of: h.weekOf,
    headline: o.weekly_summary?.headline ?? null,
    stats: {
      average_score_pct: o.kpis.avg_score,
      exams_this_week: d.academic.results.filter((r) => new Date(r.created).getTime() >= weekAgo).length,
      study_minutes_7d: e?.total_study_minutes_7d ?? null,
      study_minutes_30d: e?.total_study_minutes_30d ?? null,
      preferred_study_time: e?.preferred_study_time ?? null,
    },
    weekly_averages: (d.trend?.overall ?? []).slice(-4).map((p) => ({ week: p.week_start, avg_pct: p.avg_score })),
    chapters: (d.mastery?.student_cells ?? []).map((c) => ({
      chapter: names[c.chapter_id] ?? String(c.chapter_id), student_pct: c.pct, class_pct: cls[c.chapter_id] ?? null,
    })),
    recent_exams: [...d.academic.results]
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
      .slice(0, 5)
      .map((r) => ({ date: r.created.slice(0, 10), exam: r.exam_name, subject: r.subject, score_pct: r.score_percentage })),
    study_plan: o.study_plan?.chapters.map((c) => ({ chapter: c.name, subject: c.subject, why: c.rationale })) ?? [],
    plan_items: d.commitments?.plan_items.map((i) => ({ day: i.day, subject: i.subject_name, status: i.status })) ?? [],
    upcoming_exams: d.parent?.latest_digest?.payload.upcoming_exams ?? [],
    wins: h.wins,
    focus: h.focus,
    actions: h.actions,
    goals: { universities: o.profile.target_universities, departments: o.profile.target_departments },
  };
}

class Layout {
  y = M;
  constructor(public doc: jsPDF) {}
  get width() { return PAGE_W - 2 * M; }
  fill(c: Rgb) { this.doc.setFillColor(c[0], c[1], c[2]); }
  color(c: Rgb) { this.doc.setTextColor(c[0], c[1], c[2]); }
  draw(c: Rgb) { this.doc.setDrawColor(c[0], c[1], c[2]); }
  font(style: 'normal' | 'bold' | 'italic', size: number) { this.doc.setFont('helvetica', style); this.doc.setFontSize(size); }
  ensure(h: number) {
    if (this.y + h > PAGE_H - FOOTER_H - M) { this.doc.addPage(); this.y = M; }
  }
  heading(text: string) {
    this.ensure(40);
    this.y += 10;
    this.fill(C.gold); this.doc.rect(M, this.y - 11, 4, 16, 'F');
    this.font('bold', 13); this.color(C.dark);
    this.doc.text(text, M + 12, this.y + 1);
    this.y += 22;
  }
  paragraph(text: string, color: Rgb = C.body, size = 10, style: 'normal' | 'bold' | 'italic' = 'normal') {
    this.font(style, size); this.color(color);
    const lines = this.doc.splitTextToSize(text, this.width) as string[];
    for (const line of lines) { this.ensure(size + 5); this.doc.text(line, M, this.y); this.y += size + 5; }
    this.y += 4;
  }
  bullets(items: string[], dot: Rgb, empty: string) {
    if (items.length === 0) { this.paragraph(empty, C.muted); return; }
    for (const it of items) {
      this.font('normal', 10); this.color(C.body);
      const lines = this.doc.splitTextToSize(it, this.width - 16) as string[];
      this.ensure(lines.length * 14);
      this.fill(dot); this.doc.circle(M + 4, this.y - 3, 2.2, 'F');
      lines.forEach((l, i) => { this.doc.text(l, M + 14, this.y); if (i < lines.length - 1) this.y += 14; });
      this.y += 16;
    }
  }
  table(header: string[], rows: string[][], widths: number[]) {
    const rowH = 18;
    const drawHeader = () => {
      this.fill(C.gray2); this.doc.rect(M, this.y - 12, this.width, rowH, 'F');
      this.font('bold', 9); this.color(C.muted);
      let x = M + 6;
      header.forEach((h, i) => { this.doc.text(h, x, this.y); x += widths[i]; });
      this.y += rowH;
    };
    this.ensure(rowH * 2); drawHeader();
    this.font('normal', 9); this.color(C.dark);
    for (const r of rows) {
      if (this.y + rowH > PAGE_H - FOOTER_H - M) { this.doc.addPage(); this.y = M; drawHeader(); this.font('normal', 9); this.color(C.dark); }
      this.draw(C.stroke); this.doc.line(M, this.y - 12, M + this.width, this.y - 12);
      let x = M + 6;
      r.forEach((cell, i) => {
        const t = this.doc.splitTextToSize(String(cell), widths[i] - 10)[0] ?? '';
        this.doc.text(t, x, this.y); x += widths[i];
      });
      this.y += rowH;
    }
    this.y += 6;
  }
  stats(items: Array<{ label: string; value: string; tone?: Rgb }>) {
    const perRow = 4, gap = 8, w = (this.width - gap * (perRow - 1)) / perRow, h = 46;
    for (let i = 0; i < items.length; i += perRow) {
      this.ensure(h + 8);
      items.slice(i, i + perRow).forEach((it, j) => {
        const x = M + j * (w + gap);
        this.fill(C.gray2); this.doc.roundedRect(x, this.y - 8, w, h, 6, 6, 'F');
        this.font('normal', 8); this.color(C.muted); this.doc.text(it.label, x + 8, this.y + 4);
        this.font('bold', 13); this.color(it.tone ?? C.dark);
        this.doc.text(this.doc.splitTextToSize(it.value, w - 16)[0] ?? '', x + 8, this.y + 24);
      });
      this.y += h + 8;
    }
    this.y += 4;
  }
}

function coverBand(L: Layout, d: StudentReportData, weekOf: string) {
  const doc = L.doc;
  L.fill(C.teal); doc.rect(0, 0, PAGE_W, 120, 'F');
  L.fill(C.gold); doc.rect(0, 120, PAGE_W, 4, 'F');
  L.fill(C.white); doc.circle(M + 26, 60, 28, 'F');
  if (d.logoDataUrl) {
    doc.addImage(d.logoDataUrl, 'PNG', M + 2, 36, 48, 48);
  } else {
    L.font('bold', 22); L.color(C.teal); doc.text('Y', M + 19, 68);
  }
  L.font('bold', 20); L.color(C.white); doc.text('YADA', M + 68, 52);
  L.font('normal', 11); doc.text('Weekly report for the family', M + 68, 70);
  L.font('normal', 9); doc.text(d.schoolName || '', M + 68, 86);
  L.font('normal', 9); doc.text(`Week of ${weekOf}`, PAGE_W - M, 52, { align: 'right' });
  doc.text(`Prepared ${fmtDate(d.generatedAt)}`, PAGE_W - M, 66, { align: 'right' });
  L.y = 156;
  const o = d.overview;
  L.font('bold', 18); L.color(C.dark); doc.text(fullName(o), M, L.y);
  L.y += 18;
  const meta = [o.profile.school_grade, o.section?.display].filter(Boolean).join(' · ');
  L.font('normal', 10); L.color(C.muted); doc.text(meta || '—', M, L.y);
  L.y += 26;
}

function footers(L: Layout, d: StudentReportData) {
  const doc = L.doc, n = doc.getNumberOfPages();
  for (let p = 1; p <= n; p++) {
    doc.setPage(p);
    L.fill(C.gold); doc.rect(M, PAGE_H - FOOTER_H, PAGE_W - 2 * M, 1, 'F');
    L.font('normal', 8); L.color(C.muted);
    doc.text(`YADA · ${d.schoolName || 'School'} · ${fullName(d.overview)} · for the family`, M, PAGE_H - FOOTER_H + 14);
    doc.text(`Page ${p} of ${n}`, PAGE_W - M, PAGE_H - FOOTER_H + 14, { align: 'right' });
  }
}

export function buildStudentReportPdf(d: StudentReportData, now: Date = new Date()): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const L = new Layout(doc);
  const h = deriveWeekHighlights(d, now);
  const o = d.overview;
  const first = firstName(o);
  const e = d.engagement?.kpis;
  const weekAgo = now.getTime() - 7 * 86400000;
  const examsThisWeek = d.academic.results.filter((r) => new Date(r.created).getTime() >= weekAgo).length;
  const weakCount = (d.mastery?.student_cells ?? []).filter((c) => c.pct < 50).length;

  // Page 1 — this week, for the parent
  coverBand(L, d, h.weekOf);
  L.paragraph(`Dear parent or guardian of ${first}, here is how the week went and what would help most at home.`, C.dark, 11);
  L.stats([
    { label: 'Average score', value: pct(o.kpis.avg_score, 1), tone: o.kpis.avg_score != null && o.kpis.avg_score < 50 ? C.danger : C.dark },
    { label: 'Exams this week', value: String(examsThisWeek) },
    { label: 'Study minutes this week', value: e ? `${e.total_study_minutes_7d}` : '—' },
    { label: 'Chapters needing work', value: String(weakCount), tone: weakCount ? C.warning : C.dark },
  ]);
  if (o.weekly_summary?.headline) L.paragraph(o.weekly_summary.headline, C.teal, 11, 'bold');
  L.heading('What went well');
  L.bullets(h.wins, C.success, 'No standout gains were recorded this week; the steps below will change that.');
  L.heading(`Where ${first} should focus`);
  L.bullets(h.focus, C.danger, 'Nothing needs special attention this week.');
  L.heading('What you can do at home this week');
  L.bullets(h.actions, C.gold, 'Keep the current routine going.');
  const enc = d.parent?.latest_digest?.payload.encouragement_en;
  if (enc) L.paragraph(enc, C.teal, 10, 'italic');

  // Page 2 — the letter
  doc.addPage(); L.y = M;
  L.heading('A letter for the family');
  if (d.letter) {
    // Blank lines separate paragraphs; single line breaks (the sign-off) are kept.
    for (const para of d.letter.split(/\n\s*\n/)) {
      const lines = para.split('\n').map((l) => l.trim()).filter(Boolean);
      lines.forEach((line, i) => {
        L.font('normal', 10.5); L.color(C.dark);
        const wrapped = L.doc.splitTextToSize(line, L.width) as string[];
        for (const w of wrapped) { L.ensure(16); L.doc.text(w, M, L.y); L.y += 15.5; }
        if (i === lines.length - 1) L.y += 6;
      });
    }
  } else {
    L.paragraph('The written letter could not be prepared this time. The lists on the first page carry the same guidance.', C.muted, 10, 'italic');
  }

  // Everything about the student
  doc.addPage(); L.y = M;
  L.heading('At a glance');
  L.stats([
    { label: 'Activity on the app', value: o.kpis.engagement_dot === 'active' ? 'Active' : o.kpis.engagement_dot === 'idle' ? 'Quiet lately' : 'No activity for a week',
      tone: o.kpis.engagement_dot === 'silent' ? C.danger : C.dark },
    { label: 'Support level', value: !o.risk ? 'Not assessed yet' : o.risk.band === 'high' ? 'Needs extra support now' : o.risk.band === 'medium' ? 'Keep a close eye' : 'On track',
      tone: o.risk?.band === 'high' ? C.danger : o.risk?.band === 'medium' ? C.warning : C.dark },
    { label: 'Last active', value: fmtDate(o.kpis.last_active_at) },
    { label: 'Preferred study time', value: e?.preferred_study_time ?? '—' },
    { label: 'Study minutes (30 days)', value: e ? `${e.total_study_minutes_30d}` : '—' },
    { label: 'Minutes per active day', value: e ? `${e.avg_minutes_per_active_day}` : '—' },
    { label: 'Exams taken in total', value: e ? String(e.total_exams_taken) : String(d.academic.results.length) },
    { label: 'Chapters mastered (85%+)', value: String((d.mastery?.student_cells ?? []).filter((c) => c.pct >= 85).length) },
  ]);

  if (d.trend && d.trend.overall.length) {
    L.heading('Progress over the past weeks');
    L.table(['Week', 'Average score', 'Exams taken'],
      d.trend.overall.slice(-8).map((p) => [p.week_start, pct(p.avg_score, 1), String(p.attempts_count)]),
      [180, 120, 120]);
  }
  if (d.trend && d.trend.by_subject.length) {
    L.heading('Subjects');
    L.table(['Subject', 'Latest week', 'Week before', 'Change'],
      d.trend.by_subject.map((s) => {
        const n = s.series.length, last = s.series[n - 1], prev = s.series[n - 2];
        const delta = last && prev ? last.avg_score - prev.avg_score : null;
        return [s.subject_name, last ? pct(last.avg_score, 1) : '—', prev ? pct(prev.avg_score, 1) : '—',
          delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`];
      }), [200, 105, 105, 105]);
  }
  if (d.mastery && d.mastery.chapters.length) {
    const s: Record<number, number> = {}, c: Record<number, number | null> = {};
    d.mastery.student_cells.forEach((x) => { s[x.chapter_id] = x.pct; });
    d.mastery.class_avg_per_chapter.forEach((x) => { c[x.chapter_id] = x.pct; });
    L.heading(`Chapters: where ${first} stands next to the class`);
    L.table(['Chapter', first, 'Class', 'Status'],
      [...d.mastery.chapters]
        .sort((a, b) => (s[a.id] ?? 101) - (s[b.id] ?? 101))
        .map((ch) => [ch.name, pct(s[ch.id]), pct(c[ch.id]), masteryStatus(s[ch.id])]),
      [265, 70, 70, 110]);
    L.paragraph(`Overall: ${first} ${pct(d.mastery.student_overall_avg)} · class ${pct(d.mastery.class_overall_avg)}. "Needs work" is below 50%, "strong" is 85% or better.`, C.muted, 9);
  }
  if (d.academic.results.length) {
    L.heading('Recent exams');
    L.table(['Date', 'Exam', 'Subject', 'Score', 'Right / wrong / blank'],
      [...d.academic.results]
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        .slice(0, 15)
        .map((r) => [fmtDate(r.created), r.exam_name ?? '—', r.subject ?? '—', pct(r.score_percentage, 1), `${r.right} / ${r.wrong} / ${r.unanswered}`]),
      [70, 190, 105, 60, 90]);
  }

  L.heading("This week's study plan and promises");
  if (o.study_plan?.chapters.length) {
    L.paragraph(`The app's study plan for the week of ${o.study_plan.week_of}:`, C.dark, 10);
    L.bullets(o.study_plan.chapters.map((ch) => `${ch.name} (${ch.subject}) — ${ch.rationale}`), C.teal, '');
  }
  const items = d.commitments?.plan_items ?? [];
  if (items.length) {
    L.table(['Day', 'Subject', 'Done?'], items.map((i) => [i.day, i.subject_name, i.status === 'completed' ? 'yes' : i.status === 'skipped' ? 'skipped' : 'not yet']), [120, 240, 100]);
  }
  for (const a of d.commitments?.active ?? []) {
    L.paragraph(`${first} promised: ${a.kind.replace(/_/g, ' ')} — target ${a.target_value}, by ${a.deadline}. Milestones reached: ${(a.milestones_fired ?? []).map((m) => `${m}%`).join(', ') || 'none yet'}.`, C.body, 10);
  }
  const hist = d.commitments?.history ?? [];
  if (hist.length) {
    L.table(['Earlier promise', 'Deadline', 'Outcome'], hist.slice(0, 8).map((c) => [c.kind.replace(/_/g, ' '), c.deadline, c.status]), [220, 120, 120]);
  }
  if (!o.study_plan?.chapters.length && !items.length && !(d.commitments?.active ?? []).length && !hist.length) {
    L.paragraph('No study plan or promises recorded yet.', C.muted);
  }

  if (d.engagement) {
    const series = d.engagement.daily_minutes_series.slice(-14);
    const max = Math.max(1, ...series.map((r) => r.minutes));
    const barW = Math.min(28, (L.width - (series.length - 1) * 6) / Math.max(1, series.length)), chartH = 70;
    L.ensure(chartH + 90);   // keep the heading and the chart on one page
    L.heading('Study time, last 14 days');
    series.forEach((r, i) => {
      const x = M + i * (barW + 6), bh = (r.minutes / max) * chartH;
      L.fill(C.stroke); doc.rect(x, L.y, barW, chartH, 'F');
      L.fill(C.teal); doc.rect(x, L.y + chartH - bh, barW, bh, 'F');
      L.font('normal', 6); L.color(C.muted);
      doc.text(r.date.slice(5), x + barW / 2, L.y + chartH + 10, { align: 'center' });
    });
    L.y += chartH + 24;
    L.paragraph(`${d.engagement.kpis.total_study_minutes_7d} minutes in the last 7 days · ${d.engagement.kpis.total_study_minutes_30d} in the last 30 days.`, C.muted, 9);
  }

  const cycles = d.cycles?.cycles ?? [];
  if (cycles.length) {
    L.heading('Extra support from the school');
    for (const cyc of cycles) {
      L.paragraph(`After ${cyc.source_exam?.name ?? 'the chapter review'} (${cyc.subject.name}), the school set extra work on the chapters below${cyc.remedial_exam ? `, checked with "${cyc.remedial_exam.name}"` : ''}. Status: ${cyc.status}.`, C.dark, 10);
      L.table(['Chapter', 'Before', 'After', 'Change'],
        cyc.target_chapters.map((ch) => {
          const cell = cyc.student_cells[String(ch.chapter_id)];
          const b = cell?.baseline?.pct, a = cell?.after?.after_pct, dl = cell?.after?.delta;
          return [ch.name, pct(b), a == null ? 'not yet retested' : pct(a), dl == null ? '—' : `${dl > 0 ? '+' : ''}${dl.toFixed(0)}`];
        }), [265, 80, 100, 70]);
    }
  }

  const dg = d.parent?.latest_digest;
  if (dg?.payload.upcoming_exams.length) {
    L.heading('Upcoming exams');
    L.table(['Exam', 'Subject', 'Due'], dg.payload.upcoming_exams.map((x) => [x.name, x.subject ?? '—', x.due_date]), [260, 120, 100]);
  }

  if (o.profile.target_universities.length || o.profile.target_departments.length) {
    L.heading(`${first}'s goals`);
    L.table(['Goal', 'Choice'], [
      ['Target universities', o.profile.target_universities.join(', ') || '—'],
      ['Target departments', o.profile.target_departments.join(', ') || '—'],
    ], [160, 320]);
  }

  footers(L, d);
  return doc;
}

export function studentReportFilename(d: StudentReportData, weekOf: string): string {
  // Usernames can be phone numbers; the file is named after the student.
  const slug = fullName(d.overview).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'student';
  return `yada-weekly-report-${slug}-${weekOf}.pdf`;
}
